import React, { useState, useEffect } from 'react';
import { X, Crown, Zap, Minus, Plus } from 'lucide-react';
import { Modal, Button } from '../ui';
import { toast } from '../ui/Toaster';
import { premiumService, PremiumFeatureType, GroupUpgradeOptions, UserGroupSlotUpgradeOptions } from '../../services/premiumService';
import PaymentService from '../../services/paymentService';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  upgradeType: PremiumFeatureType;
  context?: {
    groupId?: number;
    groupName?: string;
    reason?: string;
  };
  onUpgradeComplete?: () => void;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  upgradeType,
  context,
  onUpgradeComplete,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [upgradeOptions, setUpgradeOptions] = useState<GroupUpgradeOptions | null>(null);
  const [userSlotOptions, setUserSlotOptions] = useState<UserGroupSlotUpgradeOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [_successMessage, _setSuccessMessage] = useState('');

  const getFeatureDescription = (feature: PremiumFeatureType) => {
    switch (feature) {
      case 'EXTRA_GROUP_SLOTS':
        return 'Zo te zien heb je al 3 groepen waardige groepen gevonden voor jouw aanwezigheid. Nice! Zou je ons willen steunen met de prijs van een biertje, zodat wij Solofix live kunnen houden voor iedereen? Zo ja, dan verhogen wij je maximum limiet van aantal gelijktijdige groepen.';
      case 'EXTENDED_GROUP_SIZE':
        return '<strong>Wow, je groep wordt groot!</strong> - Zou je ons willen steunen om je groepsgrootte uit te breiden? Als je aardige mensen in je groep krijgt, krijg je het vast en zeker wel weer van ze terug! Met jouw hulp houden we Solofix gratis!';
      case 'CHAT_ENHANCEMENTS':
        return 'Alle basis functies van Solofix zijn gratis! Als je het platform waardeert, kunnen we je steun heel goed gebruiken. Als dankjewel krijg je de mogelijkheid om GIFjes én foto\'s te versturen in chat voor €4,95.';
      default:
        return 'Ontgrendel deze premium feature voor €4,95';
    }
  };

  // Reset modal state when opening
  useEffect(() => {
    if (isOpen) {
      setPurchaseSuccess(false);
      _setSuccessMessage('');
      setSelectedQuantity(1);
    }
  }, [isOpen]);

  // Load upgrade options for group size upgrades
  useEffect(() => {
    if (isOpen && upgradeType === 'EXTENDED_GROUP_SIZE' && context?.groupId) {
      setLoadingOptions(true);
      premiumService.getGroupUpgradeOptions(context.groupId)
        .then(options => {
          setUpgradeOptions(options);
          setSelectedQuantity(1); // Reset to 1 when opening
        })
        .catch(error => {
          console.error('Failed to load upgrade options:', error);
          toast.error('Fout', 'Kon upgrade opties niet laden');
        })
        .finally(() => setLoadingOptions(false));
    }
  }, [isOpen, upgradeType, context?.groupId]);

  // Load upgrade options for user group slot upgrades
  useEffect(() => {
    if (isOpen && upgradeType === 'EXTRA_GROUP_SLOTS') {
      setLoadingOptions(true);
      premiumService.getUserGroupSlotUpgradeOptions()
        .then(options => {
          setUserSlotOptions(options);
          setSelectedQuantity(1); // Reset to 1 when opening
        })
        .catch(error => {
          console.error('Failed to load user slot upgrade options:', error);
          toast.error('Fout', 'Kon upgrade opties niet laden');
        })
        .finally(() => setLoadingOptions(false));
    }
  }, [isOpen, upgradeType]);

  const getFeatureBenefits = (feature: PremiumFeatureType) => {
    switch (feature) {
      case 'EXTRA_GROUP_SLOTS':
        if (userSlotOptions) {
          const newLimit = userSlotOptions.currentLimit + (selectedQuantity * 3);
          return [
            `Join tot ${Math.min(newLimit, 9)} groepen tegelijk`,
            'Meer kans om de perfecte groep te vinden',
            'Ontmoet meer mensen op festivals',
            'Flexibiliteit in groep keuzes',
          ];
        }
        return [
          'Join tot 6 groepen tegelijk',
          'Meer kans om de perfecte groep te vinden',
          'Ontmoet meer mensen op festivals',
          'Flexibiliteit in groep keuzes',
        ];
      case 'EXTENDED_GROUP_SIZE':
        if (upgradeOptions) {
          const newCapacity = upgradeOptions.currentCapacity + (selectedQuantity * 5);
          return [
            `Tot ${Math.min(newCapacity, 50)} leden in je groep`,
            'Meer diversiteit in je festivalgroep',
            'Hogere kans op succesvolle matches',
            'Leukere groepsdynamiek',
          ];
        }
        return [
          'Meer leden in je groep (max 50)',
          'Meer diversiteit in je festivalgroep',
          'Hogere kans op succesvolle matches',
          'Leukere groepsdynamiek',
        ];
      case 'CHAT_ENHANCEMENTS':
        return [
          'Stuur GIFs en foto\'s in de chat',
          'Werkt ook in directe berichten',
          'Steun het platform en houd het gratis',
          'Maak gesprekken nog leuker',
        ];
      default:
        return ['Premium voordelen'];
    }
  };

  const handlePurchase = async () => {
    try {
      setIsLoading(true);

      const request = {
        featureType: upgradeType,
        ...(context?.groupId && { groupId: context.groupId }),
        quantity: selectedQuantity,
        originUrl: window.location.pathname + window.location.search, // Track where payment started
      };
      
      // Use real Mollie payment via PaymentService
      const paymentResponse = await PaymentService.createPremiumPayment(request);

      if (paymentResponse && paymentResponse.checkoutUrl) {
        toast.success('Betaling aangemaakt!', 'Je wordt doorgestuurd naar iDEAL...');
        setTimeout(() => {
          PaymentService.redirectToCheckout(paymentResponse.checkoutUrl);
        }, 1000);
      } else {
        console.error('❌ [UpgradeModal] No checkout URL received:', paymentResponse);
        toast.error('Fout', 'Kon geen betaling aanmaken. Probeer het opnieuw.');
      }
    } catch (error: any) {
      console.error('💥 [UpgradeModal] Payment creation error:', error);
      const errorMessage = error.message || 'Er ging iets mis bij het maken van de betaling';
      toast.error('Fout', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getTotalCost = () => {
    return selectedQuantity * 4.95;
  };

  const canDecreaseQuantity = () => {
    return selectedQuantity > 1;
  };

  const canIncreaseQuantity = () => {
    if (upgradeType === 'EXTENDED_GROUP_SIZE') {
      if (!upgradeOptions) return false;
      return selectedQuantity < upgradeOptions.availableIncrements && selectedQuantity < 8;
    } else if (upgradeType === 'EXTRA_GROUP_SLOTS') {
      if (!userSlotOptions) return false;
      return selectedQuantity < userSlotOptions.availableUpgrades && selectedQuantity < 2;
    }
    return false;
  };

  const handleSuccessClose = () => {
    onUpgradeComplete?.();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="w-full">
        {/* Close button */}
        <div className="absolute top-4 right-4">
          <button
            onClick={purchaseSuccess ? handleSuccessClose : onClose}
            className="text-fg-muted hover:text-fg-bright transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 pt-12 space-y-6">
          {purchaseSuccess ? (
            /* Success Content */
            <>
              <div className="text-center space-y-4">
                <div
                  className="w-16 h-16 bg-neon-lime/[.12] rounded-full flex items-center justify-center mx-auto"
                  style={{ boxShadow: '0 0 30px rgba(184,255,61,.35)' }}
                >
                  <Crown className="h-8 w-8 text-neon-lime" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-fg-bright mb-2">
                    Bedankt voor je biertje, proost op Solofix!
                  </h3>
                  <p className="text-fg-muted">
                    Je steunbetaling is succesvol verwerkt!
                  </p>
                </div>

                {upgradeType === 'EXTENDED_GROUP_SIZE' && upgradeOptions && (
                  <div className="bg-neon-lime/[.08] border border-neon-lime/25 rounded-item p-4">
                    <p className="text-sm text-neon-lime">
                      <strong>Je groep capaciteit is vergroot!</strong><br />
                      Van {upgradeOptions.currentCapacity} naar {Math.min(upgradeOptions.currentCapacity + (selectedQuantity * 5), 50)} leden
                    </p>
                  </div>
                )}

                {upgradeType === 'EXTRA_GROUP_SLOTS' && userSlotOptions && (
                  <div className="bg-neon-lime/[.08] border border-neon-lime/25 rounded-item p-4">
                    <p className="text-sm text-neon-lime">
                      <strong>Je groep limiet is verhoogd!</strong><br />
                      Van {userSlotOptions.currentLimit} naar {Math.min(userSlotOptions.currentLimit + (selectedQuantity * 3), 9)} groepen
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Purchase Form Content */
            <>
              {/* Reason (if provided) */}
              {context?.reason && (
                <div className="bg-warning-500/10 border border-warning-500/30 rounded-item p-4">
                  <p className="text-sm text-warning-400">
                    <strong>Waarom deze steunbetaling?</strong><br />
                    {context.reason}
                  </p>
                </div>
              )}

              {/* Description */}
              <div>
                <p
                  className="text-fg-body leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: getFeatureDescription(upgradeType) }}
                />
              </div>

              {/* Benefits - only show for chat enhancements, not for group slots or group size */}
              {upgradeType === 'CHAT_ENHANCEMENTS' && (
                <div>
                  <h3 className="text-sm font-semibold text-fg-soft mb-3">
                    Wat krijg je ervoor:
                  </h3>
                  <ul className="space-y-2">
                    {getFeatureBenefits(upgradeType).map((benefit, index) => (
                      <li key={index} className="flex items-center text-sm text-fg-body">
                        <Zap className="h-4 w-4 text-neon-lime mr-3 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Quantity selector for group size upgrades - only show when not success */}
          {!purchaseSuccess && upgradeType === 'EXTENDED_GROUP_SIZE' && upgradeOptions && (
            <div className="sf-item border border-white/[.08] p-4">
              <h3 className="text-sm font-semibold text-fg-soft mb-3 text-center">
                Hoeveel wil je bijdragen?
              </h3>

              {loadingOptions ? (
                <div className="text-center text-sm text-fg-muted">Opties laden...</div>
              ) : (
                <>
                  <div className="flex items-center justify-center space-x-4 mb-4">
                    <button
                      onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                      disabled={!canDecreaseQuantity()}
                      className="w-10 h-10 rounded-full border border-white/[.14] text-fg-card flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/[.06] transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </button>

                    <div className="text-center min-w-[120px]">
                      <div className="text-2xl font-bold text-fg-bright">{selectedQuantity}</div>
                      <div className="text-xs font-mono uppercase tracking-[.14em] text-neon-cyan">
                        +{selectedQuantity * 5} plekken
                      </div>
                    </div>

                    <button
                      onClick={() => setSelectedQuantity(selectedQuantity + 1)}
                      disabled={!canIncreaseQuantity()}
                      className="w-10 h-10 rounded-full border border-white/[.14] text-fg-card flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/[.06] transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="text-xs text-center text-fg-muted">
                    Huidige capaciteit: {upgradeOptions.currentCapacity} → Nieuwe capaciteit: {Math.min(upgradeOptions.currentCapacity + (selectedQuantity * 5), 50)}
                    <br />
                    Nog {upgradeOptions.availableIncrements} uitbreidingen mogelijk (max 50 leden)
                  </div>
                </>
              )}
            </div>
          )}

          {/* Simple donation options for group slots - only show when not success */}
          {!purchaseSuccess && upgradeType === 'EXTRA_GROUP_SLOTS' && userSlotOptions && (
            <div className="sf-item border border-white/[.08] p-4">
              {loadingOptions ? (
                <div className="text-center text-sm text-fg-muted">Opties laden...</div>
              ) : (
                <div className="space-y-3">
                  <div
                    onClick={() => setSelectedQuantity(1)}
                    className={`p-3 rounded-field border cursor-pointer transition-colors ${
                      selectedQuantity === 1
                        ? 'border-neon-pink/40 bg-neon-card'
                        : 'border-white/[.08] hover:border-white/[.18]'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-fg-bright">+3 groepen</span>
                      <span className="text-sm text-fg-muted">De prijs van een biertje</span>
                    </div>
                  </div>

                  {userSlotOptions.availableUpgrades > 1 && (
                    <div
                      onClick={() => setSelectedQuantity(2)}
                      className={`p-3 rounded-field border cursor-pointer transition-colors ${
                        selectedQuantity === 2
                          ? 'border-neon-pink/40 bg-neon-card'
                          : 'border-white/[.08] hover:border-white/[.18]'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-fg-bright">+6 groepen</span>
                        <span className="text-sm text-fg-muted">De prijs van een groot biertje</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Price highlight - only show when not success */}
          {!purchaseSuccess && (
            <div className="sf-card-highlight p-4 text-center">
              <div className="text-3xl font-bold text-fg-bright mb-1">€{getTotalCost().toFixed(2)}</div>
              <div className="text-sm text-fg-muted">
                {upgradeType === 'EXTRA_GROUP_SLOTS'
                  ? (selectedQuantity === 1 ? 'De prijs van een biertje' : 'De prijs van een groot biertje')
                  : (selectedQuantity === 1 ? 'De prijs van een biertje' : `${selectedQuantity} biertjes`)
                }
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 p-6 border-t border-white/[.08]">
          {purchaseSuccess ? (
            /* Success Footer */
            <Button
              variant="primary"
              onClick={handleSuccessClose}
              className="w-full"
            >
              <Crown className="h-4 w-4 mr-2" />
              Geweldig! Ga naar Groep
            </Button>
          ) : (
            /* Purchase Footer */
            <>
              <Button
                variant="outline"
                onClick={onClose}
                className="w-full sm:flex-1"
                disabled={isLoading}
              >
                Misschien Later
              </Button>
              <Button
                variant="primary"
                onClick={handlePurchase}
                loading={isLoading}
                className="w-full sm:flex-1"
                disabled={loadingOptions}
              >
                <Crown className="h-4 w-4 mr-2" />
                Betalen met iDEAL - €{getTotalCost().toFixed(2)}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UpgradeModal;